import React, { useMemo } from 'react';
import { makeStyles, Theme, fade } from '@material-ui/core/styles';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import Input from '@material-ui/core/Input';
import { FieldArray, FormikErrors, useField } from 'formik';
import { Checkbox, FormHelperText, FormLabel } from '@material-ui/core';
import _ from 'lodash';
import Chip from './Chip';
import { titleCase } from '../lib';
import { Industry, IndustryOption } from '../types/types';

interface SelectStyleProps {
  error: boolean
}

const useStyles = makeStyles<Theme, SelectStyleProps>((theme: Theme) => ({
  root: {
    marginTop: theme.spacing(1),
    '& .MuiFormLabel-root': {
      transition: 'none',
      transform: 'none',
      margin: 0,
      fontSize: 14,
      fontWeight: 500
    },
    '& label + .MuiInput-formControl': {
      marginTop: 5
    },
    '& .MuiInput-underline': {
      '&:before': {
        content: 'none'
      },
      '&:after': {
        content: 'none'
      }
    },
    '& .MuiInput-input': {
      border: (props) => (props.error ? '1px solid #EA6172' : '1px solid #D0D6DA'),
      borderRadius: 4,
      fontSize: 16,
      fontWeight: 300,
      padding: '9px 12px',
      transition: theme.transitions.create(['border-color', 'box-shadow'], { duration: 250, delay: 0 }),
      '&:focus': {
        boxShadow: `${fade(theme.palette.primary.main, 0.25)} 0 0 0 0`,
        borderColor: theme.palette.primary.main
      }
    },
    '& .MuiSelect-root': {
      '&:focus': {
        backgroundColor: 'white'
      }
    },
    '& .MuiFormHelperText-root': {
      color: theme.palette.error.main
    }
  }
}));

interface Option<T> {
  value: T
  label: string
}

type OptionValue = Industry;

interface SelectInputProps<T extends OptionValue> {
  onBlur: any
  id: string
  label: string
  touched: boolean | undefined
  error: string | string[] | FormikErrors<Industry[]> | undefined
  multiple: boolean | undefined
  options: Option<T>[]
  value: T | T[]
}

function IndustrySelect<T extends OptionValue>({
  onBlur,
  id,
  value,
  error = undefined,
  label,
  touched = false,
  multiple = false,
  options
}: SelectInputProps<T>) {
  const classes = useStyles({ error: !!error });
  const [,, helpers] = useField({ name: id });
  const handleChange = (event: any, child: React.ReactNode) => {
    const selected = (child! as React.ReactElement).props.value as Industry;
    if (multiple) {
      const newList = (event.target as HTMLInputElement).value as unknown as Industry[];
      const countSelected = _.filter(newList, (i) => i.industry === selected.industry);
      if (countSelected.length > 1) {
        if (selected.industry === IndustryOption.Any) {
          helpers.setValue([]);
        } else {
          _.remove(newList, (i) => i.industry === selected.industry);
          helpers.setValue(newList);
        }
      } else {
        // eslint-disable-next-line no-lonely-if
        if (selected.industry === IndustryOption.Any) {
          _.remove(newList, (i) => i.industry !== selected.industry);
          helpers.setValue(newList);
        } else {
          helpers.setValue(newList);
        }
      }
    } else {
      helpers.setValue(selected);
    }
  };

  function getMenuItemStyle(item: Industry) {
    const isFound = itemSelected(item);
    if (isFound) {
      return {
        backgroundColor: '#EDF3F5',
        fontWeight: 500,
        paddingLeft: 15,
        margin: 0
      };
    } else {
      return {
        paddingLeft: 15,
        margin: 0
      };
    }
  }

  const itemSelected = (item: Industry) => {
    let isFound;
    if (multiple) {
      const idx = _.findIndex(value as Industry[], (i) => i.industry === item.industry);
      isFound = idx >= 0;
    } else {
      isFound = (value as Industry).industry === item.industry;
    }
    return isFound;
  };

  const anyIsSelected = useMemo(() => {
    try {
      if ((value as T[]).length === 1) {
        const selected = (value as T[])[0];
        if (selected.industry === IndustryOption.Any) {
          return true;
        }
      }
    } catch (e: unknown) {
      return false;
    }
    return false;
  }, [value]);

  return (
    <FormControl classes={classes} fullWidth>
      <FormLabel id={id} htmlFor={id}>{label}</FormLabel>
      <Select
        fullWidth
        labelId={id}
        id={id}
        multiple={multiple}
        name={id}
        value={value}
        onChange={handleChange}
        MenuProps={{ variant: 'menu' }}
        onBlur={onBlur}
        input={<Input />}
        defaultValue={multiple ? [{ id: null, industry: '' }] : { id: null, industry: '' }}
        renderValue={(v: any) => {
          if (multiple) {
            const val = v as OptionValue[];
            const lab = val.map((i) => (i as Industry).industry);
            return (
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {lab.map((i) => <Chip key={titleCase(i)} label={titleCase(i)} />)}
              </div>
            );
          } else {
            const val = v as OptionValue;
            const lab = (val as Industry).industry;
            return (<div>{titleCase(lab)}</div>);
          }
        }}
      >
        {options.map((item) => {
          const selected = itemSelected(item.value);
          const isAny = item.value.industry === IndustryOption.Any;
          return (
            <MenuItem style={getMenuItemStyle(item.value)} key={item.label} value={(item.value as any)} disabled={anyIsSelected && !isAny}>
              {multiple ? <Checkbox checked={selected || anyIsSelected} /> : null }
              {titleCase(item.label as string)}
            </MenuItem>
          );
        })}
      </Select>
      {touched && error ? <FormHelperText>{error}</FormHelperText> : null}
    </FormControl>
  );
}

export default IndustrySelect;
